import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { copyByLanguage } from "../../config/copy";
import { createPreviewServices, type InvitationServices } from "../../services/invitation-services";
import type { CanvasElementLayout, GalleryImage, WeddingData, WishRecord } from "../../types/wedding";
import { BarcodeModal } from "../components/BarcodeModal";
import { GalleryViewer } from "../components/GalleryViewer";
import { GiftModal } from "../components/GiftModal";
import { InvitationNavigation } from "../components/InvitationNavigation";
import { MusicPlayer } from "../components/MusicPlayer";
import { sectionRegistry } from "../section-registry";
import { groupSectionsByBackground, sectionBackgroundClassName, sectionBackgroundStyle, sectionBackgroundVideoSource } from "../section-background";
import { applyCanvasLayout, registerCanvasTargets } from "../canvas-layout";
import { CanvasLayoutEditor, type CanvasSelection } from "./CanvasLayoutEditor";

export type InvitationView = "cover" | "invitation";

interface Props {
  data: WeddingData;
  services?: InvitationServices;
  mode?: "reference-preview" | "builder" | "public";
  onFieldSelect?: (selection: { section: string; field?: string }) => void;
  builderView?: InvitationView;
  builderZoom?: number;
  onInvitationViewChange?: (view: InvitationView) => void;
  onCanvasLayoutChange?: (elementId: string, layout: CanvasElementLayout | null) => void;
  onInlineTextChange?: (field: string, value: string) => void;
}

interface GalleryState {
  images: GalleryImage[];
  index: number | null;
}

function valueAtPath(value: unknown, path: string) {
  return path.split(".").reduce<unknown>((current, part) => current && typeof current === "object" ? (current as Record<string, unknown>)[part] : undefined, value);
}

function editableTextField(selection: CanvasSelection | null, data: WeddingData) {
  if (!selection || !["H1", "H2", "H3", "H4", "P", "SPAN", "BUTTON", "A", "BLOCKQUOTE", "SMALL", "STRONG"].includes(selection.element.tagName)) return null;
  const field = selection.element.closest<HTMLElement>("[data-editor-field]")?.dataset.editorField;
  if (!field) return null;
  const value = valueAtPath(data, field);
  return typeof value === "string" ? { field, value } : null;
}

export function InvitationRenderer({ data, services = createPreviewServices(), mode = "public", onFieldSelect, builderView, builderZoom, onInvitationViewChange, onCanvasLayoutChange, onInlineTextChange }: Props) {
  const [invitationOpen, setInvitationOpen] = useState(mode === "builder" ? builderView !== "cover" : false);
  const [warmTransition, setWarmTransition] = useState(false);
  const [playSignal, setPlaySignal] = useState(0);
  const [giftOpen, setGiftOpen] = useState(false);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [gallery, setGallery] = useState<GalleryState>({ images: [], index: null });
  const [wishes, setWishes] = useState<WishRecord[]>([]);
  const [canvasSelection, setCanvasSelection] = useState<CanvasSelection | null>(null);
  const [inlineEditRequest, setInlineEditRequest] = useState(0);
  const rendererRef = useRef<HTMLDivElement>(null);
  const lastEditorPointerRef = useRef<{ time: number; x: number; y: number } | null>(null);
  const copy = copyByLanguage[data.project.language] || copyByLanguage.id;

  const refreshWishes = useCallback(async () => {
    setWishes(await services.wishes.list(false));
  }, [services]);

  useEffect(() => {
    void refreshWishes();
  }, [refreshWishes]);

  useEffect(() => {
    if (mode === "builder" && builderView) setInvitationOpen(builderView === "invitation");
  }, [builderView, mode]);

  useEffect(() => {
    document.body.classList.add("paper-lines");
    document.documentElement.style.scrollBehavior = "smooth";
    if (mode === "public" && !window.location.hash.includes("section")) window.scrollTo(0, 0);
    return () => document.body.classList.remove("paper-lines");
  }, [mode]);

  useEffect(() => {
    const root = rendererRef.current;
    if (!root) return;
    const applyAll = () => {
      for (const target of registerCanvasTargets(root)) {
        const elementId = target.dataset.canvasId;
        if (elementId) applyCanvasLayout(target, data.layout?.elements?.[elementId], window.innerWidth);
      }
    };
    applyAll();
    window.addEventListener("resize", applyAll);
    return () => window.removeEventListener("resize", applyAll);
  }, [data]);

  const sections = useMemo(() => data.sections.slice().filter((section) => section.enabled).sort((a, b) => a.order - b.order), [data.sections]);
  const backgroundGroups = useMemo(() => groupSectionsByBackground(sections), [sections]);
  const themeStyle = {
    "--paper": data.theme.backgroundColor,
    "--ink": data.theme.textColor,
    "--sage": data.theme.secondaryColor,
    "--moss": data.theme.primaryColor,
    "--clay": data.theme.accentColor,
    "--heading-font": data.theme.headingFont,
    "--body-font": data.theme.bodyFont,
    "--section-space": data.theme.sectionSpacing
  } as CSSProperties;

  const openInvitation = () => {
    setWarmTransition(true);
    window.setTimeout(() => {
      setInvitationOpen(true);
      onInvitationViewChange?.("invitation");
    }, data.theme.animationStyle === "none" ? 0 : 620);
    window.setTimeout(() => setWarmTransition(false), 1300);
    setPlaySignal((value) => value + 1);
  };

  const openGallery = (images: GalleryImage[], index: number) => setGallery({ images, index });

  const handleEditorClick = (event: MouseEvent<HTMLDivElement>) => {
    if (mode !== "builder") return;
    if (event.target instanceof Element && event.target.closest("[data-canvas-ui]")) return;
    const canvasTarget = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-canvas-id]") : null;
    if (canvasTarget && onCanvasLayoutChange) {
      event.preventDefault();
      event.stopPropagation();
      setCanvasSelection({ id: canvasTarget.dataset.canvasId!, element: canvasTarget });
    } else if (onCanvasLayoutChange) {
      setCanvasSelection(null);
    }
    if (!onFieldSelect) return;
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-editor-field], [data-editor-section]") : null;
    if (!target) return;
    const section = target.dataset.editorSection || target.closest<HTMLElement>("[data-editor-section]")?.dataset.editorSection;
    if (section) onFieldSelect({ section, field: target.dataset.editorField });
  };

  const handleEditorDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (mode !== "builder" || !onInlineTextChange) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(".canvas-drag-surface")) {
      event.preventDefault();
      event.stopPropagation();
      setInlineEditRequest((value) => value + 1);
      return;
    }
    if (target?.closest("[data-canvas-ui]")) return;
    const canvasTarget = target?.closest<HTMLElement>("[data-canvas-id]");
    if (!canvasTarget) return;
    event.preventDefault();
    event.stopPropagation();
    setCanvasSelection({ id: canvasTarget.dataset.canvasId!, element: canvasTarget });
    setInlineEditRequest((value) => value + 1);
  };

  const handleEditorPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (mode !== "builder" || !onInlineTextChange || event.button !== 0) return;
    const now = Date.now();
    const previous = lastEditorPointerRef.current;
    lastEditorPointerRef.current = { time: now, x: event.clientX, y: event.clientY };
    if (!previous || now - previous.time > 450 || Math.hypot(event.clientX - previous.x, event.clientY - previous.y) > 14) return;
    const target = event.target instanceof Element ? event.target : null;
    const canvasTarget = target?.closest<HTMLElement>("[data-canvas-id]") || (target?.closest(".canvas-drag-surface") ? canvasSelection?.element : null);
    const nextSelection = canvasTarget ? { id: canvasTarget.dataset.canvasId!, element: canvasTarget } : canvasSelection;
    if (!editableTextField(nextSelection, data)) return;
    event.preventDefault();
    event.stopPropagation();
    if (nextSelection) setCanvasSelection(nextSelection);
    setInlineEditRequest((value) => value + 1);
    lastEditorPointerRef.current = null;
  };

  const inlineField = editableTextField(canvasSelection, data);

  return (
    <div ref={rendererRef} className={`invitation-renderer preview-${mode} animation-${data.theme.animationStyle}`} style={themeStyle} onPointerDownCapture={handleEditorPointerDown} onClickCapture={handleEditorClick} onDoubleClickCapture={handleEditorDoubleClick}>
      <InvitationNavigation
        data={data}
        onOpenGift={() => setGiftOpen(true)}
        onOpenBarcode={() => setBarcodeOpen(true)}
        musicPlayer={<MusicPlayer music={data.music} copy={copy} playSignal={playSignal} />}
      />
      {backgroundGroups.map((group) => (
        <div
          className={sectionBackgroundClassName(group.background)}
          data-background-group={group.background.mode === "shared" ? group.background.group : undefined}
          data-editor-section="backgrounds"
          key={group.key}
          style={sectionBackgroundStyle(group.background)}
        >
          {sectionBackgroundVideoSource(group.background) ? <><video className="section-background-video" key={sectionBackgroundVideoSource(group.background)} src={sectionBackgroundVideoSource(group.background)} autoPlay muted loop playsInline preload="metadata" aria-hidden="true" /><div className="section-background-video-shade" aria-hidden="true" /></> : null}
          <div className="section-background-content">
            {group.sections.map((config) => {
              const Section = sectionRegistry[config.type];
              const content = (
                <Section
                  key={config.id}
                  data={data}
                  config={config}
                  copy={copy}
                  services={services}
                  previewMode={mode}
                  invitationOpen={invitationOpen}
                  onOpenInvitation={openInvitation}
                  onOpenGift={() => setGiftOpen(true)}
                  onOpenGallery={openGallery}
                  wishes={wishes}
                  refreshWishes={refreshWishes}
                />
              );
              return <div className="section-background-page" key={config.id}>{mode === "builder" ? <div className="builder-section-target" data-editor-section={config.type}>{content}</div> : <Fragment>{content}</Fragment>}</div>;
            })}
          </div>
        </div>
      ))}
      <div className={`warm-transition ${warmTransition ? "play" : ""}`} aria-hidden="true" />
      <GalleryViewer images={gallery.images} index={gallery.index} copy={copy} onChange={(index) => setGallery((value) => ({ ...value, index }))} onClose={() => setGallery({ images: [], index: null })} />
      <GiftModal gifts={data.gifts} open={giftOpen} copy={copy} onClose={() => setGiftOpen(false)} />
      <BarcodeModal barcode={data.barcode} open={barcodeOpen} closeLabel={copy.close} onClose={() => setBarcodeOpen(false)} />
      {mode === "builder" && onCanvasLayoutChange ? <CanvasLayoutEditor rootRef={rendererRef} selection={canvasSelection} layouts={data.layout?.elements || {}} onSelectionChange={setCanvasSelection} onCommit={onCanvasLayoutChange} editableField={inlineField?.field} editableValue={inlineField?.value} inlineEditRequest={inlineEditRequest} onTextCommit={onInlineTextChange} editorZoom={builderZoom} /> : null}
    </div>
  );
}
